import { useEffect, useMemo, useRef, useState } from "react";
import { Tab, Tabs } from "react-bootstrap";
import { useQueryClient } from "@tanstack/react-query";
import { useEntityDetail, useIdsOnly, useInfiniteListEntity } from "../../api/BaseEntityApi";
import { LucideIcon } from "../../common/icon/LucideIcon";
import { ELNDeleteConfirmationModal } from "../ELNCreateOrEditForms/ELNDeleteConfirmationModal";
import { ELNModes } from "../ELNRouter/ELNRouter";
import { useELNRoutes } from "../ELNRouter/useELNRoutes";
import { LabNotebookEntriesPaginated } from "../LabNotebookEntriesPaginated/LabNotebookEntriesPaginated";
import { ELNSaveStatus } from "../common/TextEditor/TextEditor";
import { TextEditorSidebarTile } from "../common/TextEditor/components/TextEditorSidebarTile/TextEditorSidebarTile";
import { LAB_NOTEBOOK_TOC_QUERY_KEY, LabNotebookTocTile } from "../common/tiles/LabNotebookTocTile/LabNotebookTocTile";
import { LabNotebookFilters, LabNotebook as LabNotebookReadModel, labNoteBooksConstants } from "../types/LabNotebook";
import {
  labNotebookEntriesConstants,
  LabNotebookEntryFilters,
  LabNotebookEntry as LabNotebookEntryReadModel,
} from "../types/LabNotebookEntry";
import {
  LabNotebookExperiment,
  LabNotebookExperimentFilters,
  labNotebookExperimentsConstants,
} from "../types/LabNotebookExperiment";
import styles from "./LabNotebook.module.css";
import { QuickAddWrapper } from "../../common/forms/QuickAdd/common/QuickAddWrapper";
import { QuickAddLabNotebookExperimentForm } from "../../common/forms/QuickAdd/forms/QuickAddLabNotebookExperiment";
import { QuickEditWrapper } from "../../common/forms/QuickEdit/common/QuickEditWrapper";
import { QuickEditLabNotebookExperimentForm } from "../../common/forms/QuickEdit/forms/QuickEditLabNotebookExperiment";
import { QuickAddLabNotebookEntryForm } from "../../common/forms/QuickAdd/forms/QuickAddLabNotebookEntry";
import { QuickEditLabNotebookEntryForm } from "../../common/forms/QuickEdit/forms/QuickEditLabNotebookEntry";
import { QuickAddLabNotebookForm } from "../../common/forms/QuickAdd/forms/QuickAddLabNotebook";
import { QuickEditLabNotebookForm } from "../../common/forms/QuickEdit/forms/QuickEditLabNotebook";
import { useDefaultEntryCreateMutation } from "../common/tiles/LabNotebookExperimentsTile";
import { getNotebookEntryStringLabel, LabNotebookEntry } from "../LabNotebookEntry/LabNotebookEntry";
import Detail from "../../common/panels/Detail/Detail";
import { GenericEntityHistoryModal } from "../../Entities/GenericComponents/common/GenericEntityHistoryModal";
import {
  DetailsPageTopControlsView,
  TagProps,
} from "../../common/panels/misc/DetailsPageTopControlsView/DetailsPageTopControlsView";
import { EntityBreadcrumbs } from "../../common/hierarchy/breadcrumbs/EntityBreadcrumbs";
import { EntityRestoreButton } from "../../common/entity/entityComponents/EntityRestoreButton";
import { EntityComplianceDropdown } from "../../common/entity/entityComponents/EntityComplianceDropdown";
import { EntityDeleteButton } from "../../Entities/GenericComponents/common/GenericEntityDetailPage";
import { useEntityPermissions } from "../../common/permissions/useEntityPermissions";
import { useEntityApi } from "../../api/useEntityApi";
import { EntityByEntityTypeId } from "../../api/GenericConstants";
import { NestedDropdown } from "../../common/buttons/NestedDropdown/NestedDropdown";
import { projectsConstants } from "../../api/Projects";
import { Button } from "../../common/buttons/Button/Button";
import { Redirect } from "react-router-dom";
import { EntityEditButton } from "../../common/entity/entityComponents/EntityEditButton";
import { useEntityRoutes } from "../../common/routes/useEntityRoutes";
import { LoadingWrapper } from "../../common/LoadingWrapper";
import { ExportPdfButton } from "../../common/tables/ExportPdfButton/ExportPdfButton";
import { GenericEntityExportButton } from "../../Entities/GenericComponents/common/GenericEntityExportButton";

export const LabNotebook = ({
  labNotebookId,
  labNotebookExperimentId,
  labNotebookEntryId,
  mode,
  labNotebookBlockId,
}: {
  labNotebookId?: number;
  labNotebookExperimentId?: number;
  labNotebookEntryId?: number;
  mode?: ELNModes;
  labNotebookBlockId?: string;
}) => {
  // hooks
  const { setELNRoute, getELNRoute, getQueryParam } = useELNRoutes();

  const createDefaultEntry = useDefaultEntryCreateMutation();

  // Modal states
  const [showCreateLabNotebookModal, setShowCreateLabNotebookModal] = useState<boolean>(false);
  const [showEditLabNotebookModal, setShowEditLabNotebookModal] = useState<boolean>(false);
  const [showDeleteLabNotebookModal, setShowDeleteLabNotebookModal] = useState<boolean>(false);
  const [showLabNotebookDetailsHistory, setShowLabNotebookDetailsHistory] = useState<boolean>(false);

  const [showCreateLabNotebookExperimentModal, setShowCreateLabNotebookExperimentModal] = useState<boolean>(false);
  const [showEditLabNotebookExperimentModal, setShowEditLabNotebookExperimentModal] = useState<boolean>(false);
  const [showDeleteLabNotebookExperimentModal, setShowDeleteLabNotebookExperimentModal] = useState<boolean>(false);
  const [showLabNotebookExperimentDetailsHistory, setShowLabNotebookExperimentDetailsHistory] =
    useState<boolean>(false);

  const [showCreateLabNotebookEntryModal, setShowCreateLabNotebookEntryModal] = useState<boolean>(false);
  const [showEditLabNotebookEntryModal, setShowEditLabNotebookEntryModal] = useState<boolean>(false);
  const [showDeleteLabNotebookEntryModal, setShowDeleteLabNotebookEntryModal] = useState<boolean>(false);
  const [showLabNotebookEntryHistory, setShowLabNotebookEntryHistory] = useState<boolean>(false);

  const [filters, setFilters] = useState<LabNotebookEntryFilters>({});

  useEffect(() => {
    const urlFilters = JSON.parse(getQueryParam("filters") || "{}");
    if (!!Object.keys(urlFilters).length) {
      setFilters(urlFilters);
    }
  }, [getQueryParam]);

  // LAB NOTEBOOK
  const {
    data: currentLabNotebook,
    refetch: refetchLabNotebook,
    status: labNotebookStatus,
    error: labNotebookError,
    fetchStatus: labNotebookFetchStatus,
  } = useEntityDetail<LabNotebookReadModel, LabNotebookFilters>(
    "lab_notebooks",
    labNotebookId ?? 0,
    {},
    {
      enabled: labNotebookId !== undefined,
      retry: false,
      onError: (e) => {
        setELNRoute({ replace: true });
      },
    }
  );

  // LAB NOTEBOOK EXPERIMENT
  const {
    data: currentLabNotebookExperiment,
    refetch: refetchLabNotebookExperiment,
    status: labNotebookExperimentStatus,
    error: labNotebookExperimentError,
    fetchStatus: labNotebookExperimentFetchStatus,
  } = useEntityDetail<LabNotebookExperiment, LabNotebookExperimentFilters>(
    "lab_notebook_experiments",
    labNotebookExperimentId || 0,
    {},
    {
      enabled: labNotebookExperimentId !== undefined,
      retry: false,
      onError: (e) => {
        setELNRoute({ labNotebookId, replace: true });
      },
    }
  );

  // LAB NOTEBOOK ENTRY
  const {
    data: currentLabNotebookEntry,
    refetch: refetchLabNotebookEntry,
    status: labNotebookEntryStatus,
    error: labNotebookEntryError,
    fetchStatus: labNotebookEntryFetchStatus,
  } = useEntityDetail<LabNotebookEntryReadModel, LabNotebookEntryFilters>(
    "lab_notebook_entries",
    labNotebookEntryId || 0,
    { includeContent: false },
    {
      enabled: labNotebookEntryId !== undefined,
      retry: false,
      onError: (e) => {
        setELNRoute({ labNotebookId, labNotebookExperimentId, replace: true });
      },
    }
  );

  // add filters based on entities
  const combinedFilters = useMemo(
    () => ({
      ...filters,
      includeSoftDeleted:
        !!currentLabNotebook?.isDeleted ||
        !!currentLabNotebookExperiment?.isDeleted ||
        !!currentLabNotebookEntry?.isDeleted ||
        filters.includeSoftDeleted,
    }),
    [
      filters,
      currentLabNotebook?.isDeleted,
      currentLabNotebookEntry?.isDeleted,
      currentLabNotebookExperiment?.isDeleted,
    ]
  );

  const { data: experimentsData, isFetching: isFetchingExperiments } = useIdsOnly<number, LabNotebookExperimentFilters>(
    "lab_notebook_experiments",
    {
      labNotebookIds: labNotebookId ? [labNotebookId] : undefined,
      page: 1,
      pageSize: 1,
      sortBy: [
        {
          property: "ENTERED_ON",
          sortDirection: "DESC",
        },
      ],
    },
    { enabled: !!labNotebookId && !labNotebookExperimentId }
  );
  const { data: entriesData, isFetching: isFetchingEntries } = useInfiniteListEntity<
    LabNotebookEntryReadModel,
    LabNotebookEntryFilters
  >(
    "lab_notebook_entries",
    {
      ...combinedFilters,
      labNotebookReferenceIds: !!labNotebookId ? [labNotebookId] : undefined,
      labNotebookExperimentReferenceIds: !!labNotebookExperimentId ? [labNotebookExperimentId] : undefined,
      page: 1,
      pageSize: 1,
    },
    { enabled: !labNotebookEntryId },
    "post"
  );

  const entries = useMemo(() => {
    return entriesData?.pages.map((d) => d.results).flat() || [];
  }, [entriesData?.pages]);

  const [isCheckingURL, setIsCheckingURL] = useState<boolean>(false);
  useEffect(() => {
    setIsCheckingURL(!labNotebookEntryId || !labNotebookExperimentId);
    if (!isFetchingEntries && !isFetchingExperiments) {
      if (!labNotebookEntryId && !!entries.length) {
        const timeout = setTimeout(() => {
          setELNRoute({
            labNotebookId,
            labNotebookExperimentId: entries[0].labNotebookExperiment.id,
            labNotebookEntryId: entries[0].id,
            replace: true,
          });
        }, 300);

        return () => clearTimeout(timeout);
      } else {
        if (!labNotebookExperimentId && !!experimentsData?.results.length && entries && !entries.length) {
          const timeout = setTimeout(() => {
            setELNRoute({
              labNotebookId,
              labNotebookExperimentId: experimentsData.results[0],
              replace: true,
            });
          }, 300);

          return () => clearTimeout(timeout);
        }
      }
    }
    setIsCheckingURL(false);
  }, [
    currentLabNotebookEntry,
    entries,
    experimentsData?.results,
    isFetchingEntries,
    isFetchingExperiments,
    labNotebookEntryId,
    labNotebookExperimentId,
    labNotebookId,
    setELNRoute,
  ]);

  // editor functions
  const remirrorContextRef = useRef<any>(null);
  const [saveStatus, setSaveStatus] = useState<ELNSaveStatus>({ isSaved: true, isSaving: false, isError: false });

  const queryClient = useQueryClient();

  const [activeSidebarTab, setActiveSidebarTab] = useState<string>("Toc");

  // update sidebar if mode changes
  useEffect(() => {
    if (mode !== "edit") setActiveSidebarTab((prevState) => (prevState === "AddContent" ? "Toc" : prevState));
  }, [mode]);

  const entryApi = useEntityApi<EntityByEntityTypeId<"notebookEntries">>(labNotebookEntriesConstants.resource);
  const currentEntryPermissions = useEntityPermissions({ entityTypeId: "notebookEntries" });
  const currentEntryRoutes = useEntityRoutes({ entityConstants: labNotebookEntriesConstants });

  const experimentApi = useEntityApi<EntityByEntityTypeId<"notebookExperiments">>(
    labNotebookExperimentsConstants.resource
  );
  const currentExperimentPermissions = useEntityPermissions({ entityTypeId: "notebookExperiments" });

  const notebookApi = useEntityApi<EntityByEntityTypeId<"notebooks">>(labNoteBooksConstants.resource);
  const currentNotebookPermissions = useEntityPermissions({ entityTypeId: "notebooks" });

  const tags = useMemo(() => {
    var _tags: TagProps[] = [];
    // Entry states
    if (mode === "edit") {
      _tags.push({
        label: saveStatus.isSaved ? "Saved" : saveStatus.isError ? "Failed" : "Saving...",
        className: saveStatus.isSaved
          ? "label label-xs label-soft-success"
          : saveStatus.isError
            ? "label label-xs label-soft-danger"
            : "label label-xs label-soft-warning",
        icon: "save",
        title: saveStatus.isSaved ? "The content has been saved." : saveStatus.isError ? "Saving failed." : "Saving...",
      });
    }

    currentLabNotebook?.projects.forEach((p) => {
      _tags.push({
        label: p.name,
        className: "label label-xs label-soft-secondary",
        icon: projectsConstants.icon,
        title: p.name,
      });
    });

    // Notebook states
    currentLabNotebook?.isDeleted &&
      _tags.push({
        label: "Notebook trashed",
        className: "label label-xs label-soft-warning",
        icon: "trash-2",
      });
    currentLabNotebook?.isSigned &&
      _tags.push({
        label: "Notebook signed",
        className: "label label-xs label-soft-primary",
        icon: "signature",
      });
    currentLabNotebook?.isLocked &&
      _tags.push({
        label: "Notebook locked",
        className: "label label-xs label-soft-info",
        icon: "lock",
      });

    // Experiment states
    currentLabNotebookExperiment?.isDeleted &&
      _tags.push({
        label: "Experiment trashed",
        className: "label label-xs label-soft-warning",
        icon: "trash-2",
      });
    currentLabNotebookExperiment?.isSigned &&
      _tags.push({
        label: "Experiment signed",
        className: "label label-xs label-soft-primary",
        icon: "signature",
      });
    currentLabNotebookExperiment?.isLocked &&
      _tags.push({
        label: "Experiment locked",
        className: "label label-xs label-soft-info",
        icon: "lock",
      });
    return _tags;
  }, [
    currentLabNotebook?.isDeleted,
    currentLabNotebook?.isLocked,
    currentLabNotebook?.isSigned,
    currentLabNotebook?.projects,
    currentLabNotebookExperiment?.isDeleted,
    currentLabNotebookExperiment?.isLocked,
    currentLabNotebookExperiment?.isSigned,
    mode,
    saveStatus.isError,
    saveStatus.isSaved,
  ]);

  if (!!currentLabNotebookEntry && !currentLabNotebookEntry?.permissions?.canEdit && mode === "edit")
    return (
      <Redirect
        to={getELNRoute({
          labNotebookId: currentLabNotebook?.id,
          labNotebookExperimentId: currentLabNotebookExperiment?.id,
          labNotebookEntryId: currentLabNotebookEntry.id,
          mode: "view",
        })}
      />
    );

  return (
    <>
      <LoadingWrapper status={labNotebookStatus} fetchStatus={labNotebookFetchStatus} error={labNotebookError}>
        {!!currentLabNotebook && (
          <Detail
            head={
              <LoadingWrapper
                status={labNotebookExperimentStatus}
                fetchStatus={labNotebookExperimentFetchStatus}
                error={labNotebookExperimentError}
                type="skeleton-rows"
              >
                <LoadingWrapper
                  status={labNotebookEntryStatus}
                  fetchStatus={labNotebookEntryFetchStatus}
                  error={labNotebookEntryError}
                  type="skeleton-rows"
                >
                  <DetailsPageTopControlsView
                    entityTypeId={
                      !!currentLabNotebookEntry
                        ? "notebookEntries"
                        : !!currentLabNotebookExperiment
                          ? "notebookExperiments"
                          : "notebooks"
                    }
                    entity={currentLabNotebookEntry ?? currentLabNotebookExperiment ?? currentLabNotebook}
                    entityConstants={
                      !!currentLabNotebookEntry
                        ? labNotebookEntriesConstants
                        : !!currentLabNotebookExperiment
                          ? labNotebookExperimentsConstants
                          : labNoteBooksConstants
                    }
                    fullTitleOverride={
                      <EntityBreadcrumbs
                        entity={currentLabNotebookEntry ?? currentLabNotebookExperiment ?? currentLabNotebook}
                        hideIndex
                        maxWidth={500}
                        breadcrumbs={
                          !!currentLabNotebookEntry && !!currentLabNotebookExperiment
                            ? [
                                {
                                  entity: currentLabNotebook,
                                  entityConstants: {
                                    ...labNoteBooksConstants,
                                    icon: currentLabNotebook.lockedOn ? "book" : "book-open",
                                  },
                                  ancestorLinkToOverride: () =>
                                    getELNRoute({
                                      labNotebookId: currentLabNotebook.id,
                                    }),
                                },
                                {
                                  entity: currentLabNotebookExperiment,
                                  entityConstants: labNotebookExperimentsConstants,
                                  ancestorLinkToOverride: () =>
                                    getELNRoute({
                                      labNotebookId: currentLabNotebook.id,
                                      labNotebookExperimentId: currentLabNotebookExperiment.id,
                                    }),
                                },
                              ]
                            : !!currentLabNotebookExperiment
                              ? [
                                  {
                                    entity: currentLabNotebook,
                                    entityConstants: {
                                      ...labNoteBooksConstants,
                                      icon: currentLabNotebook.lockedOn ? "book" : "book-open",
                                    },
                                    ancestorLinkToOverride: () =>
                                      getELNRoute({
                                        labNotebookId: currentLabNotebook.id,
                                      }),
                                  },
                                ]
                              : []
                        }
                        entityConstants={
                          !!currentLabNotebookEntry
                            ? labNotebookEntriesConstants
                            : !!currentLabNotebookExperiment
                              ? labNotebookExperimentsConstants
                              : labNoteBooksConstants
                        }
                      />
                    }
                    tags={tags}
                    controls={
                      <>
                        {!!currentLabNotebookEntry && mode !== "edit" && (
                          <EntityEditButton<"notebookEntries">
                            entity={currentLabNotebookEntry}
                            entityConstants={labNotebookEntriesConstants}
                            permissions={currentEntryPermissions}
                            routes={{
                              ...currentEntryRoutes,
                              getEditLink: () =>
                                getELNRoute({
                                  labNotebookId: currentLabNotebook.id,
                                  labNotebookExperimentId: currentLabNotebookExperiment?.id,
                                  labNotebookEntryId: currentLabNotebookEntry.id,
                                  mode: "edit",
                                }),
                            }}
                            labelOverride="Edit content"
                          />
                        )}
                        {!!currentLabNotebookEntry && (
                          <>
                            <EntityRestoreButton<"notebookEntries">
                              entity={currentLabNotebookEntry}
                              entityConstants={labNotebookEntriesConstants}
                              entityApi={entryApi}
                              permissions={currentEntryPermissions}
                            />
                          </>
                        )}
                      </>
                    }
                    controlsDropdown={
                      <>
                        {currentLabNotebook && (
                          <NestedDropdown label="Notebook" icon={labNoteBooksConstants.icon} style={{ left: "-14px" }}>
                            <li>
                              <Button
                                className="btn btn-block btn-sm btn-ghost-primary flex align-center gap-5"
                                onClick={() => setShowCreateLabNotebookModal(true)}
                                disabled={!currentNotebookPermissions.canCreate}
                              >
                                <LucideIcon name="plus" />
                                Create new notebook
                              </Button>
                            </li>
                            <li className="divider" />
                            <li>
                              <Button
                                className="btn btn-block btn-sm btn-ghost-secondary flex align-center gap-5"
                                onClick={() => setShowEditLabNotebookModal(true)}
                                disabled={!currentLabNotebook.permissions?.canEdit || !!currentLabNotebook.isDeleted}
                              >
                                <LucideIcon name="square-pen" />
                                Edit notebook details
                              </Button>
                            </li>
                            <li>
                              <ExportPdfButton<LabNotebookEntryReadModel, LabNotebookEntryFilters>
                                entityConstants={labNotebookEntriesConstants}
                                filters={{ labNotebookReferenceIds: [currentLabNotebook.id] }}
                                title="Export current notebook to PDF"
                                defaultPdfSettings={{
                                  fileName: currentLabNotebook.name,
                                }}
                              />
                            </li>
                            <EntityComplianceDropdown
                              entity={currentLabNotebook}
                              entityTypeId="notebooks"
                              showSignButton
                              propOverrides={{
                                GenericEntityLockButtonProps: {
                                  componentProps: {
                                    onSuccess: () => {
                                      queryClient.invalidateQueries({ queryKey: [LAB_NOTEBOOK_TOC_QUERY_KEY] });
                                      queryClient.invalidateQueries({ queryKey: [labNoteBooksConstants.resource] });
                                      queryClient.invalidateQueries({
                                        queryKey: [labNotebookExperimentsConstants.resource],
                                      });
                                      queryClient.invalidateQueries({
                                        queryKey: [labNotebookEntriesConstants.resource],
                                      });
                                    },
                                  },
                                },
                                GenericEntitySignButtonProps: {
                                  componentProps: {
                                    onSuccess: () => {
                                      queryClient.invalidateQueries({ queryKey: [LAB_NOTEBOOK_TOC_QUERY_KEY] });
                                      queryClient.invalidateQueries({ queryKey: [labNoteBooksConstants.resource] });
                                      queryClient.invalidateQueries({
                                        queryKey: [labNotebookExperimentsConstants.resource],
                                      });
                                      queryClient.invalidateQueries({
                                        queryKey: [labNotebookEntriesConstants.resource],
                                      });
                                    },
                                  },
                                },
                                GenericEntityUnlockButtonProps: {
                                  componentProps: {
                                    onSuccess: () => {
                                      queryClient.invalidateQueries({ queryKey: [LAB_NOTEBOOK_TOC_QUERY_KEY] });
                                      queryClient.invalidateQueries({ queryKey: [labNoteBooksConstants.resource] });
                                      queryClient.invalidateQueries({
                                        queryKey: [labNotebookExperimentsConstants.resource],
                                      });
                                      queryClient.invalidateQueries({
                                        queryKey: [labNotebookEntriesConstants.resource],
                                      });
                                    },
                                  },
                                },
                              }}
                            />
                            <li>
                              <EntityRestoreButton<"notebooks">
                                entity={currentLabNotebook}
                                entityConstants={labNoteBooksConstants}
                                entityApi={notebookApi}
                                permissions={currentNotebookPermissions}
                                classNameOverride="btn btn-block btn-sm btn-ghost-success flex align-center gap-5"
                                onSuccess={() => {
                                  queryClient.invalidateQueries({ queryKey: [LAB_NOTEBOOK_TOC_QUERY_KEY] });
                                  queryClient.invalidateQueries({ queryKey: [labNoteBooksConstants.resource] });
                                  queryClient.invalidateQueries({
                                    queryKey: [labNotebookExperimentsConstants.resource],
                                  });
                                  queryClient.invalidateQueries({ queryKey: [labNotebookEntriesConstants.resource] });
                                }}
                              />
                            </li>
                            <li className="divider" />
                            <li>
                              <EntityDeleteButton entity={currentLabNotebook} entityTypeId="notebooks" />
                            </li>
                          </NestedDropdown>
                        )}
                        {!!currentLabNotebook && (
                          <NestedDropdown
                            label="Experiment"
                            icon={labNotebookExperimentsConstants.icon}
                            style={{ left: "-22px" }}
                          >
                            <li>
                              <Button
                                className="btn btn-block btn-sm btn-ghost-primary flex align-center gap-5"
                                onClick={() => setShowCreateLabNotebookExperimentModal(true)}
                                disabled={
                                  !currentExperimentPermissions.canCreate || !currentLabNotebook.permissions?.canEdit
                                }
                              >
                                <LucideIcon name="plus" />
                                Create new experiment
                              </Button>
                            </li>
                            {!!currentLabNotebookExperiment && (
                              <>
                                <li className="divider" />
                                <li>
                                  <Button
                                    className="btn btn-block btn-sm btn-ghost-secondary flex align-center gap-5"
                                    onClick={() => setShowEditLabNotebookExperimentModal(true)}
                                    disabled={
                                      !currentLabNotebookExperiment.permissions?.canEdit ||
                                      !!currentLabNotebookExperiment.isDeleted
                                    }
                                  >
                                    <LucideIcon name="square-pen" />
                                    Edit experiment details
                                  </Button>
                                </li>
                                <li>
                                  <ExportPdfButton<LabNotebookEntryReadModel, LabNotebookEntryFilters>
                                    entityConstants={labNotebookEntriesConstants}
                                    filters={{
                                      labNotebookExperimentReferenceIds: [currentLabNotebookExperiment.id],
                                    }}
                                    title="Export current experiment to PDF"
                                    defaultPdfSettings={{
                                      fileName: currentLabNotebookExperiment.name,
                                    }}
                                  />
                                </li>
                                <EntityComplianceDropdown
                                  entity={currentLabNotebookExperiment}
                                  entityTypeId="notebookExperiments"
                                  showSignButton
                                  propOverrides={{
                                    GenericEntityLockButtonProps: {
                                      componentProps: {
                                        onSuccess: () => {
                                          queryClient.invalidateQueries({ queryKey: [LAB_NOTEBOOK_TOC_QUERY_KEY] });
                                          queryClient.invalidateQueries({ queryKey: [labNoteBooksConstants.resource] });
                                          queryClient.invalidateQueries({
                                            queryKey: [labNotebookExperimentsConstants.resource],
                                          });
                                          queryClient.invalidateQueries({
                                            queryKey: [labNotebookEntriesConstants.resource],
                                          });
                                        },
                                      },
                                    },
                                    GenericEntitySignButtonProps: {
                                      componentProps: {
                                        onSuccess: () => {
                                          queryClient.invalidateQueries({ queryKey: [LAB_NOTEBOOK_TOC_QUERY_KEY] });
                                          queryClient.invalidateQueries({ queryKey: [labNoteBooksConstants.resource] });
                                          queryClient.invalidateQueries({
                                            queryKey: [labNotebookExperimentsConstants.resource],
                                          });
                                          queryClient.invalidateQueries({
                                            queryKey: [labNotebookEntriesConstants.resource],
                                          });
                                        },
                                      },
                                    },
                                    GenericEntityUnlockButtonProps: {
                                      componentProps: {
                                        onSuccess: () => {
                                          queryClient.invalidateQueries({ queryKey: [LAB_NOTEBOOK_TOC_QUERY_KEY] });
                                          queryClient.invalidateQueries({ queryKey: [labNoteBooksConstants.resource] });
                                          queryClient.invalidateQueries({
                                            queryKey: [labNotebookExperimentsConstants.resource],
                                          });
                                          queryClient.invalidateQueries({
                                            queryKey: [labNotebookEntriesConstants.resource],
                                          });
                                        },
                                      },
                                    },
                                  }}
                                />
                                <li>
                                  <EntityRestoreButton<"notebookExperiments">
                                    entity={currentLabNotebookExperiment}
                                    entityConstants={labNotebookExperimentsConstants}
                                    entityApi={experimentApi}
                                    permissions={currentExperimentPermissions}
                                    classNameOverride="btn btn-block btn-sm btn-ghost-success flex align-center gap-5"
                                    onSuccess={() => {
                                      queryClient.invalidateQueries({ queryKey: [LAB_NOTEBOOK_TOC_QUERY_KEY] });
                                      queryClient.invalidateQueries({ queryKey: [labNoteBooksConstants.resource] });
                                      queryClient.invalidateQueries({
                                        queryKey: [labNotebookExperimentsConstants.resource],
                                      });
                                      queryClient.invalidateQueries({
                                        queryKey: [labNotebookEntriesConstants.resource],
                                      });
                                    }}
                                  />
                                </li>
                                <li className="divider" />
                                <li>
                                  <EntityDeleteButton
                                    entity={currentLabNotebookExperiment}
                                    entityTypeId="notebookExperiments"
                                  />
                                </li>
                              </>
                            )}
                          </NestedDropdown>
                        )}
                        {!!currentLabNotebookExperiment && (
                          <NestedDropdown
                            label="Entry"
                            icon={labNotebookEntriesConstants.icon}
                            style={{ left: "-9px" }}
                          >
                            <li>
                              <Button
                                className="btn btn-block btn-sm btn-ghost-primary flex align-center gap-5"
                                onClick={() => setShowCreateLabNotebookEntryModal(true)}
                                disabled={
                                  !currentEntryPermissions.canCreate ||
                                  !currentLabNotebookExperiment.permissions?.canEdit ||
                                  !currentLabNotebook.permissions?.canEdit
                                }
                              >
                                <LucideIcon name="plus" />
                                Create new entry
                              </Button>
                            </li>
                            {!!currentLabNotebookEntry && (
                              <>
                                <li className="divider" />
                                <li>
                                  <Button
                                    className="btn btn-block btn-sm btn-ghost-secondary flex align-center gap-5"
                                    onClick={() => setShowEditLabNotebookEntryModal(true)}
                                    disabled={
                                      !currentLabNotebookEntry.permissions?.canEdit ||
                                      !!currentLabNotebookEntry.isDeleted
                                    }
                                  >
                                    <LucideIcon name="square-pen" />
                                    Edit entry details
                                  </Button>
                                </li>
                                <li>
                                  <GenericEntityExportButton
                                    entityTypeId="notebookEntries"
                                    entity={currentLabNotebookEntry}
                                    componentProps={{ title: "Export current entry to PDF" }}
                                  />
                                </li>
                                <EntityComplianceDropdown
                                  entity={currentLabNotebookEntry}
                                  entityTypeId="notebookEntries"
                                  showSignButton
                                  propOverrides={{
                                    GenericEntityLockButtonProps: {
                                      componentProps: {
                                        onSuccess: () => {
                                          queryClient.invalidateQueries({ queryKey: [LAB_NOTEBOOK_TOC_QUERY_KEY] });
                                          queryClient.invalidateQueries({ queryKey: [labNoteBooksConstants.resource] });
                                          queryClient.invalidateQueries({
                                            queryKey: [labNotebookExperimentsConstants.resource],
                                          });
                                          queryClient.invalidateQueries({
                                            queryKey: [labNotebookEntriesConstants.resource],
                                          });
                                        },
                                      },
                                    },
                                    GenericEntitySignButtonProps: {
                                      componentProps: {
                                        onSuccess: () => {
                                          queryClient.invalidateQueries({ queryKey: [LAB_NOTEBOOK_TOC_QUERY_KEY] });
                                          queryClient.invalidateQueries({ queryKey: [labNoteBooksConstants.resource] });
                                          queryClient.invalidateQueries({
                                            queryKey: [labNotebookExperimentsConstants.resource],
                                          });
                                          queryClient.invalidateQueries({
                                            queryKey: [labNotebookEntriesConstants.resource],
                                          });
                                        },
                                      },
                                    },
                                    GenericEntityUnlockButtonProps: {
                                      componentProps: {
                                        onSuccess: () => {
                                          queryClient.invalidateQueries({ queryKey: [LAB_NOTEBOOK_TOC_QUERY_KEY] });
                                          queryClient.invalidateQueries({ queryKey: [labNoteBooksConstants.resource] });
                                          queryClient.invalidateQueries({
                                            queryKey: [labNotebookExperimentsConstants.resource],
                                          });
                                          queryClient.invalidateQueries({
                                            queryKey: [labNotebookEntriesConstants.resource],
                                          });
                                        },
                                      },
                                    },
                                  }}
                                />
                                <li>
                                  <EntityRestoreButton<"notebookEntries">
                                    entity={currentLabNotebookEntry}
                                    entityConstants={labNotebookEntriesConstants}
                                    entityApi={entryApi}
                                    permissions={currentEntryPermissions}
                                    classNameOverride="btn btn-block btn-sm btn-ghost-success flex align-center gap-5"
                                    onSuccess={() => {
                                      queryClient.invalidateQueries({ queryKey: [LAB_NOTEBOOK_TOC_QUERY_KEY] });
                                      queryClient.invalidateQueries({ queryKey: [labNoteBooksConstants.resource] });
                                      queryClient.invalidateQueries({
                                        queryKey: [labNotebookExperimentsConstants.resource],
                                      });
                                      queryClient.invalidateQueries({
                                        queryKey: [labNotebookEntriesConstants.resource],
                                      });
                                    }}
                                  />
                                </li>
                                <li className="divider" />
                                <li>
                                  <EntityDeleteButton entity={currentLabNotebookEntry} entityTypeId="notebookEntries" />
                                </li>
                              </>
                            )}
                          </NestedDropdown>
                        )}
                      </>
                    }
                  />
                </LoadingWrapper>
              </LoadingWrapper>
            }
            main={
              <div
                className={styles.LabNotebookContentContainer}
                style={{
                  backgroundColor: "var(--gray-100)",
                  minWidth: "210mm",
                  height: "100%",
                  overflow: "hidden",
                }}
              >
                {mode === "edit" && labNotebookEntryId ? (
                  <LabNotebookEntry
                    labNotebookId={labNotebookId}
                    labNotebookExperimentId={labNotebookExperimentId}
                    labNotebookEntryId={labNotebookEntryId}
                    labNotebookBlockId={labNotebookBlockId}
                    mode={mode}
                    remirrorContextRef={remirrorContextRef}
                    setSaveStatus={setSaveStatus}
                  />
                ) : (
                  <LabNotebookEntriesPaginated
                    labNotebookId={labNotebookId}
                    labNotebookExperimentId={labNotebookExperimentId}
                    labNotebookEntryId={labNotebookEntryId}
                    mode={mode}
                    labNotebookBlockId={labNotebookBlockId}
                    setShowCreateLabNotebookEntry={() => setShowCreateLabNotebookEntryModal(true)}
                    setShowCreateLabNotebookExperiment={() => setShowCreateLabNotebookExperimentModal(true)}
                    filters={{
                      ...filters,
                      labNotebookReferenceIds: [currentLabNotebook.id],
                      includeSoftDeleted:
                        !!currentLabNotebook.isDeleted ||
                        !!currentLabNotebookExperiment?.isDeleted ||
                        !!currentLabNotebookEntry?.isDeleted ||
                        filters.includeSoftDeleted,
                    }}
                    showCreateButtonsOnEmpty={!!currentLabNotebook.permissions?.canEdit}
                    isLoading={
                      (!labNotebookExperimentId && isFetchingExperiments) ||
                      (!labNotebookEntryId && isFetchingEntries) ||
                      isCheckingURL
                    }
                  />
                )}
              </div>
            }
            sidebar={
              <>
                <Tabs
                  defaultActiveKey="Toc"
                  activeKey={activeSidebarTab}
                  id="tabs-menue"
                  bsStyle="tabs"
                  onSelect={(k) => setActiveSidebarTab(k)}
                  justified
                  style={{ backgroundColor: "var(--gray-50)", padding: "5px 5px 0px 5px" }}
                >
                  <Tab
                    eventKey="Toc"
                    title={
                      <div
                        style={{
                          display: "grid",
                          alignItems: "center",
                          width: "100%",
                        }}
                      >
                        <div
                          data-toggle="tooltip"
                          title="Table of content"
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            width: "100%",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <LucideIcon name="menu" />
                          <span> Table of content</span>
                        </div>
                      </div>
                    }
                  />
                  {mode === "edit" && (
                    <Tab
                      eventKey="AddContent"
                      disabled={mode !== "edit"}
                      title={
                        <div
                          style={{
                            display: "grid",
                            alignItems: "center",
                            width: "100%",
                          }}
                        >
                          <div
                            data-toggle="tooltip"
                            title="Add content"
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              width: "100%",
                              whiteSpace: "nowrap",
                              color: "var(--primary)",
                            }}
                          >
                            <LucideIcon name="plus" />
                            <span> Add content</span>
                          </div>
                        </div>
                      }
                    />
                  )}
                </Tabs>

                <div style={{ overflow: "auto", padding: "0 10px", height: "100%" }}>
                  {activeSidebarTab === "Toc" && (
                    <LabNotebookTocTile
                      labNotebookId={currentLabNotebook.id}
                      labNotebookExperimentId={currentLabNotebookExperiment?.id}
                      labNotebookEntryId={currentLabNotebookEntry?.id}
                      mode={mode}
                      activeLabNotebookBlockId={labNotebookBlockId}
                      filters={combinedFilters}
                      setFilters={setFilters}
                    />
                  )}
                  {activeSidebarTab === "AddContent" && (
                    <TextEditorSidebarTile remirrorContextRef={remirrorContextRef} />
                  )}
                </div>
              </>
            }
            sidebarPosition="left"
            sidebarStyle={{ padding: 0, margin: 0, height: "100%" }}
            sidebarContentStyle={{ padding: 0, margin: 0, height: "100%" }}
            mainMinSize={50}
            mainDefaultSize={70}
            sidebarMinSize={30}
          />
        )}
      </LoadingWrapper>

      {/* Notebook Modals */}
      {/* Create */}
      {showCreateLabNotebookModal && (
        <QuickAddWrapper<LabNotebookReadModel>
          showModal={showCreateLabNotebookModal}
          setShowModal={setShowCreateLabNotebookModal}
          onCreate={(newNotebook) => {
            if (newNotebook && newNotebook.id > 0) {
              queryClient.invalidateQueries({ queryKey: [LAB_NOTEBOOK_TOC_QUERY_KEY] });
              queryClient.invalidateQueries({ queryKey: [labNoteBooksConstants.resource] });
              queryClient.invalidateQueries({ queryKey: [labNotebookExperimentsConstants.resource] });
              queryClient.invalidateQueries({ queryKey: [labNotebookEntriesConstants.resource] });
              setELNRoute({ labNotebookId: newNotebook.id });
            }
          }}
        >
          {(props) => <QuickAddLabNotebookForm {...props} />}
        </QuickAddWrapper>
      )}

      {/* Edit */}
      {!!currentLabNotebook && showEditLabNotebookModal && (
        <QuickEditWrapper<LabNotebookReadModel>
          showModal={showEditLabNotebookModal}
          setShowModal={setShowEditLabNotebookModal}
          onEdit={(editedLabNotebook) => {
            queryClient.invalidateQueries({ queryKey: [LAB_NOTEBOOK_TOC_QUERY_KEY] });
            queryClient.invalidateQueries({ queryKey: [labNoteBooksConstants.resource] });
          }}
        >
          {(props) => <QuickEditLabNotebookForm {...props} initialValues={currentLabNotebook} />}
        </QuickEditWrapper>
      )}

      {/* Delete */}
      {!!currentLabNotebook && (
        <ELNDeleteConfirmationModal
          isOpen={showDeleteLabNotebookModal}
          setIsOpen={setShowDeleteLabNotebookModal}
          id={currentLabNotebook.id}
          name={currentLabNotebook.name.length > 25 ? "notebook" : currentLabNotebook.name}
          entityInfo={{ "Lab notebook": currentLabNotebook.name, "Description": currentLabNotebook.description }}
          resource="lab_notebooks"
          onDelete={() => {
            queryClient.invalidateQueries({ queryKey: [LAB_NOTEBOOK_TOC_QUERY_KEY] });
            queryClient.invalidateQueries({ queryKey: [labNoteBooksConstants.resource] });
            queryClient.invalidateQueries({ queryKey: [labNotebookExperimentsConstants.resource] });
            queryClient.invalidateQueries({ queryKey: [labNotebookEntriesConstants.resource] });
            setShowDeleteLabNotebookModal(false);
          }}
          description="Proceeding will move the selected notebook into trash."
          deletePermanently={false}
        />
      )}
      {/* History */}
      {!!currentLabNotebook && (
        <GenericEntityHistoryModal
          entityTypeId="notebooks"
          entity={currentLabNotebook}
          componentProps={{
            hideTriggerComponent: true,
            forceOpen: showLabNotebookDetailsHistory,
            onRestore: () => {
              setShowLabNotebookDetailsHistory(false);
              refetchLabNotebook();
            },
            onModalClose: () => setShowLabNotebookDetailsHistory(false),
          }}
        />
      )}

      {/* Experiment Modals */}
      {/* Create */}
      {showCreateLabNotebookExperimentModal && !!currentLabNotebook && (
        <QuickAddWrapper<LabNotebookExperiment>
          showModal={showCreateLabNotebookExperimentModal}
          setShowModal={setShowCreateLabNotebookExperimentModal}
          onCreate={(newExperiment) => {
            if (newExperiment && newExperiment.id > 0) {
              queryClient.invalidateQueries({ queryKey: [LAB_NOTEBOOK_TOC_QUERY_KEY] });
              queryClient.invalidateQueries({ queryKey: [labNoteBooksConstants.resource] });
              queryClient.invalidateQueries({ queryKey: [labNotebookExperimentsConstants.resource] });
              queryClient.invalidateQueries({ queryKey: [labNotebookEntriesConstants.resource] });
              createDefaultEntry(newExperiment);
              setELNRoute({ labNotebookId: newExperiment.labNotebook.id, labNotebookExperimentId: newExperiment.id });
            }
          }}
        >
          {(props) => (
            <QuickAddLabNotebookExperimentForm
              {...props}
              initialValues={{ labNotebook: currentLabNotebook, status: "RUNNING" }}
            />
          )}
        </QuickAddWrapper>
      )}

      {/* Edit */}
      {!!currentLabNotebookExperiment && showEditLabNotebookExperimentModal && (
        <QuickEditWrapper<LabNotebookExperiment>
          showModal={showEditLabNotebookExperimentModal}
          setShowModal={setShowEditLabNotebookExperimentModal}
          onEdit={(editedLabNotebookExperiment) => {
            queryClient.invalidateQueries({ queryKey: [LAB_NOTEBOOK_TOC_QUERY_KEY] });
            queryClient.invalidateQueries({ queryKey: [labNoteBooksConstants.resource] });
            queryClient.invalidateQueries({ queryKey: [labNotebookExperimentsConstants.resource] });
          }}
        >
          {(props) => <QuickEditLabNotebookExperimentForm {...props} initialValues={currentLabNotebookExperiment} />}
        </QuickEditWrapper>
      )}

      {/* Delete */}
      {!!currentLabNotebookExperiment && showDeleteLabNotebookExperimentModal && (
        <ELNDeleteConfirmationModal
          isOpen={showDeleteLabNotebookExperimentModal}
          setIsOpen={setShowDeleteLabNotebookExperimentModal}
          id={currentLabNotebookExperiment.id}
          name={currentLabNotebookExperiment.name.length > 25 ? "experiment" : currentLabNotebookExperiment.name}
          entityInfo={{
            "Experiment": currentLabNotebookExperiment.name,
            "Description": currentLabNotebookExperiment.description,
            "Part of": currentLabNotebookExperiment.labNotebook.name,
          }}
          resource="lab_notebook_experiments"
          onDelete={() => {
            queryClient.invalidateQueries({ queryKey: [LAB_NOTEBOOK_TOC_QUERY_KEY] });
            queryClient.invalidateQueries({ queryKey: [labNoteBooksConstants.resource] });
            queryClient.invalidateQueries({ queryKey: [labNotebookExperimentsConstants.resource] });
            queryClient.invalidateQueries({ queryKey: [labNotebookEntriesConstants.resource] });
            setShowDeleteLabNotebookExperimentModal(false);
          }}
          description="Proceeding will move the selected experiment into trash."
          deletePermanently={false}
        />
      )}
      {/* History */}
      {!!currentLabNotebookExperiment && (
        <GenericEntityHistoryModal
          entityTypeId="notebookExperiments"
          entity={currentLabNotebookExperiment}
          componentProps={{
            hideTriggerComponent: true,
            forceOpen: showLabNotebookExperimentDetailsHistory,
            onRestore: () => {
              setShowLabNotebookExperimentDetailsHistory(false);
              refetchLabNotebookExperiment();
            },
            onModalClose: () => setShowLabNotebookExperimentDetailsHistory(false),
          }}
        />
      )}

      {/* Entry Modals */}
      {/* Create */}
      {showCreateLabNotebookEntryModal && !!currentLabNotebook && !!currentLabNotebookExperiment && (
        <QuickAddWrapper<LabNotebookEntryReadModel>
          showModal={showCreateLabNotebookEntryModal}
          setShowModal={setShowCreateLabNotebookEntryModal}
          onCreate={(newEntry) => {
            if (newEntry && newEntry.id > 0) {
              queryClient.invalidateQueries({ queryKey: [LAB_NOTEBOOK_TOC_QUERY_KEY] });
              queryClient.invalidateQueries({ queryKey: [labNoteBooksConstants.resource] });
              queryClient.invalidateQueries({ queryKey: [labNotebookExperimentsConstants.resource] });
              queryClient.invalidateQueries({ queryKey: [labNotebookEntriesConstants.resource] });
              setELNRoute({
                labNotebookId: newEntry.labNotebook.id,
                labNotebookExperimentId: newEntry.labNotebookExperiment.id,
                labNotebookEntryId: newEntry.id,
              });
            }
          }}
        >
          {(props) => (
            <QuickAddLabNotebookEntryForm
              {...props}
              initialValues={{
                labNotebookExperiment: currentLabNotebookExperiment,
                labNotebook: currentLabNotebook,
                entryDate: new Date().toISOString(),
              }}
            />
          )}
        </QuickAddWrapper>
      )}

      {/* Edit */}
      {!!currentLabNotebookEntry && showEditLabNotebookEntryModal && (
        <QuickEditWrapper<LabNotebookEntryReadModel>
          showModal={showEditLabNotebookEntryModal}
          setShowModal={setShowEditLabNotebookEntryModal}
          onEdit={(editedLabNotebookEntry) => {
            queryClient.invalidateQueries({ queryKey: [LAB_NOTEBOOK_TOC_QUERY_KEY] });
            queryClient.invalidateQueries({ queryKey: [labNoteBooksConstants.resource] });
            queryClient.invalidateQueries({ queryKey: [labNotebookExperimentsConstants.resource] });
            queryClient.invalidateQueries({ queryKey: [labNotebookEntriesConstants.resource] });
          }}
        >
          {(props) => <QuickEditLabNotebookEntryForm {...props} initialValues={currentLabNotebookEntry} />}
        </QuickEditWrapper>
      )}

      {/* Delete */}
      {!!currentLabNotebookEntry && showDeleteLabNotebookEntryModal && (
        <ELNDeleteConfirmationModal
          isOpen={showDeleteLabNotebookEntryModal}
          setIsOpen={setShowDeleteLabNotebookEntryModal}
          id={currentLabNotebookEntry.id}
          name={getNotebookEntryStringLabel({ entry: currentLabNotebookEntry })}
          entityInfo={{
            "Entry": getNotebookEntryStringLabel({ entry: currentLabNotebookEntry }),
            "Part of":
              currentLabNotebookEntry.labNotebook.name + " - " + currentLabNotebookEntry.labNotebookExperiment.name,
          }}
          resource="lab_notebook_entries"
          onDelete={() => {
            queryClient.invalidateQueries({ queryKey: [LAB_NOTEBOOK_TOC_QUERY_KEY] });
            queryClient.invalidateQueries({ queryKey: [labNotebookEntriesConstants.resource] });
            setShowDeleteLabNotebookEntryModal(false);
          }}
          description="Proceeding will move the selected entry into trash."
          deletePermanently={false}
        />
      )}
      {/* History */}
      {!!currentLabNotebookEntry && (
        <GenericEntityHistoryModal
          entityTypeId="notebookEntries"
          entity={currentLabNotebookEntry}
          componentProps={{
            hideTriggerComponent: true,
            forceOpen: showLabNotebookEntryHistory,
            onRestore: () => {
              setShowLabNotebookEntryHistory(false);
              refetchLabNotebookEntry();
              setELNRoute({ labNotebookId, labNotebookExperimentId, labNotebookEntryId, mode: "view" });
            },
            onModalClose: () => setShowLabNotebookEntryHistory(false),
          }}
        />
      )}
    </>
  );
};
